import { ARIA, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * @class FullWidthDropdown
 * @extends MessageBus
 * @description Manages expand/collapse behaviour for a full-width FAQ-style
 * accordion. Clicking an item's toggle opens its drawer and collapses any
 * previously open item, so only one drawer is ever visible at a time.
 */
export default class FullWidthDropdown extends MessageBus {
  /**
   * @param {HTMLElement} element - The module's root container element.
   */
  constructor(element) {
    super();

    /** @type {HTMLElement} */
    this.element = element;

    /** @type {HTMLElement[]} An array of accordion item elements. */
    this.items = [];

    /** @type {HTMLElement[]} An array of toggle elements. */
    this.toggles = [];

    this.init();
  }

  /**
   * Initialize the module and wire up handlers.
   * @returns {this} The instance for chaining.
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    this.openHashTarget();

    return this;
  }

  /**
   * Cache DOM references used by the component.
   * @returns {this} The instance for chaining.
   */
  cacheDomReferences() {
    this.items = Array.from(
      this.element.querySelectorAll(SELECTORS.FULL_WIDTH_DROPDOWN_ITEM)
    );

    this.toggles = Array.from(
      this.element.querySelectorAll(SELECTORS.FULL_WIDTH_DROPDOWN_TOGGLE)
    );

    return this;
  }

  /**
   * Bind event handler methods to `this`.
   * @returns {this} The instance for chaining.
   */
  setupHandlers() {
    this.handleToggleClick = this.onToggleClick.bind(this);
    this.handleHashChange = this.openHashTarget.bind(this);

    return this;
  }

  /**
   * Attach event listeners and enable component behaviour.
   * @returns {this} The instance for chaining.
   */
  enable() {
    if (!this.toggles.length) return this;

    this.toggles.forEach((toggle) => {
      toggle.addEventListener(EVENTS.CLICK, this.handleToggleClick);
    });
    window.addEventListener('hashchange', this.handleHashChange);

    return this;
  }

  /**
   * Remove event listeners and clean up.
   * @returns {this} The instance for chaining.
   */
  destroy() {
    window.removeEventListener('hashchange', this.handleHashChange);
    this.toggles.forEach((toggle) => {
      toggle.removeEventListener(EVENTS.CLICK, this.handleToggleClick);
    });

    return this;
  }

  /**
   * Expand the question containing the current URL anchor.
   * @returns {void}
   */
  openHashTarget() {
    let id;
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch (error) {
      return;
    }
    if (!id) return;

    const target = document.getElementById(id);
    const item = target && target.closest(SELECTORS.FULL_WIDTH_DROPDOWN_ITEM);
    if (!item || !this.items.includes(item)) return;

    const toggle = item.querySelector(SELECTORS.FULL_WIDTH_DROPDOWN_TOGGLE);
    const drawer = item.querySelector(SELECTORS.FULL_WIDTH_DROPDOWN_DRAWER);
    this.collapseAll(item);
    item.setAttribute(ARIA.EXPANDED, 'true');
    if (toggle) toggle.setAttribute(ARIA.EXPANDED, 'true');
    if (drawer) drawer.setAttribute(ARIA.HIDDEN, 'false');
    target.scrollIntoView({ block: 'start' });
  }

  /**
   * Collapse all items except the one provided.
   * @param {HTMLElement} [activeItem] - The item to leave open. When omitted,
   *   every item is collapsed.
   * @returns {void}
   */
  collapseAll(activeItem) {
    this.items.forEach((item) => {
      if (item === activeItem) return;

      const toggle = item.querySelector(SELECTORS.FULL_WIDTH_DROPDOWN_TOGGLE);
      const drawer = item.querySelector(SELECTORS.FULL_WIDTH_DROPDOWN_DRAWER);

      item.setAttribute(ARIA.EXPANDED, 'false');

      if (toggle) toggle.setAttribute(ARIA.EXPANDED, 'false');
      if (drawer) drawer.setAttribute(ARIA.HIDDEN, 'true');
    });
  }

  /**
   * Toggle the clicked item open or closed, collapsing all other items first.
   * @param {MouseEvent} event - Click event from the toggle element.
   * @returns {void}
   */
  onToggleClick(event) {
    const toggle = event.currentTarget;
    const item = toggle.closest(SELECTORS.FULL_WIDTH_DROPDOWN_ITEM);
    const drawer = item
      ? item.querySelector(SELECTORS.FULL_WIDTH_DROPDOWN_DRAWER)
      : null;

    if (!item) return;

    const isOpen = toggle.getAttribute(ARIA.EXPANDED) === 'true';

    this.collapseAll(item);

    const nextState = isOpen ? 'false' : 'true';

    item.setAttribute(ARIA.EXPANDED, nextState);
    toggle.setAttribute(ARIA.EXPANDED, nextState);

    if (drawer) drawer.setAttribute(ARIA.HIDDEN, isOpen ? 'true' : 'false');
  }
}
